/**
 * Prompt Dropdown Menu
 *
 * Displays organized prompt library in a dropdown:
 * - Search bar
 * - Favorites & Recently Used sections
 * - Category-based organization
 * - Quick access to full library modal
 *
 * Designed for non-technical users (medical staff) - no slash commands.
 */

import type { PromptCategory, PromptTemplate } from '@ctrl-enter/types';
import {
  PROMPT_CATEGORIES,
  PROMPT_CATEGORY_ICONS,
  PROMPT_CATEGORY_LABELS,
} from '@ctrl-enter/types';
import { Clock, Library, Search, Sparkles, Star, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useAllPrompts,
  useFavoritePrompts,
  useRecentlyUsedPrompts,
} from '../hooks/use-prompt-library';
import { usePromptLibraryStore } from '../stores/prompt-library-store';
import { cn } from '../utils/cn';
import { searchPrompts } from '../utils/prompt-utils';
import { PromptCategoryIcon } from './prompt-category-icon';

type PromptDropdownMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectPrompt: (prompt: PromptTemplate) => void;
  onOpenFullLibrary: () => void;
  anchorRef: React.RefObject<HTMLElement>;
};

export const PromptDropdownMenu: React.FC<PromptDropdownMenuProps> = ({
  isOpen,
  onClose,
  onSelectPrompt,
  onOpenFullLibrary,
  anchorRef,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<PromptCategory | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const expandedCategoryRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const allPrompts = useAllPrompts();
  const favoritePrompts = useFavoritePrompts();
  const recentlyUsedPrompts = useRecentlyUsedPrompts();

  // ✅ CORRECT - Use selector, not destructuring (per .cursorrules)
  const isFavorite = usePromptLibraryStore(state => state.isFavorite);
  const isPromptsLoading = usePromptLibraryStore(state => state.isPromptsLoading);
  const isPreferencesLoading = usePromptLibraryStore(state => state.isPreferencesLoading);

  // Show loading if either prompts or preferences are loading
  const isLoading = isPromptsLoading || isPreferencesLoading;

  // Get recently added prompts (newest first)
  const recentlyAddedPrompts = useMemo(() => {
    return [...allPrompts].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 5);
  }, [allPrompts]);

  // Filter prompts by search query
  const filteredPrompts = searchQuery.trim() ? searchPrompts(allPrompts, searchQuery) : allPrompts;

  // Get category counts
  const categoryCounts = PROMPT_CATEGORIES.reduce(
    (acc, category) => {
      acc[category] = filteredPrompts.filter(p => p.category === category).length;
      return acc;
    },
    {} as Record<PromptCategory, number>
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking the anchor button
      if (anchorRef.current?.contains(target)) {
        return;
      }

      // Don't close if clicking inside dropdown
      if (dropdownRef.current?.contains(target)) {
        return;
      }

      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Position dropdown relative to anchor (opens to the LEFT)
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties | null>(null);

  React.useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) {
      setDropdownStyle(null);
      return;
    }

    const updatePosition = () => {
      if (!anchorRef.current) return;

      const anchorRect = anchorRef.current.getBoundingClientRect();

      if (isMobile) {
        setDropdownStyle({
          position: 'fixed',
          bottom: window.innerHeight - anchorRect.top + 8,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '300px',
          maxHeight: '400px',
          zIndex: 9999,
        });
      } else {
        setDropdownStyle({
          position: 'fixed',
          bottom: window.innerHeight - anchorRect.top + 8,
          right: window.innerWidth - anchorRect.right,
          width: '360px',
          maxHeight: '400px',
          zIndex: 9999,
        });
      }
    };

    updatePosition();

    // Update on window resize
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isOpen, anchorRef, isMobile]);

  const handlePromptClick = (prompt: PromptTemplate) => {
    onSelectPrompt(prompt);
    onClose();
    setSearchQuery('');
    setExpandedCategory(null);
  };

  const handleCategoryClick = (category: PromptCategory) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  // Scroll to expanded category
  useEffect(() => {
    if (expandedCategory && expandedCategoryRef.current && scrollContainerRef.current) {
      // Small delay to allow animation to start
      setTimeout(() => {
        expandedCategoryRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [expandedCategory]);

  if (!isOpen || !dropdownStyle) return null;

  // Render dropdown as portal to prevent layout shifts
  const dropdownContent = (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className={cn(
        'bg-popover border border-border rounded-lg shadow-lg',
        'animate-in fade-in-0 zoom-in-95 duration-200',
        'flex flex-col overflow-hidden'
      )}
    >
      {/* Search bar */}
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={cn(
              'w-full pl-9 pr-8 py-2 text-sm text-foreground',
              'glass-surface border border-border/50 rounded-xl',
              'placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
              'transition-all duration-200'
            )}
            autoFocus
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-accent rounded"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto max-h-[320px]">
        {isLoading ? (
          /* Loading state */
          <div className="p-2 space-y-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="px-3 py-2 rounded-md glass-surface-bg glass-surface-border animate-pulse"
              >
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 rounded bg-muted/20 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-muted/20 rounded w-3/4" />
                    <div className="h-3 bg-muted/20 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : searchQuery.trim() ? (
          /* Search results */
          <div className="p-2">
            {filteredPrompts.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                No prompts found for "{searchQuery}"
              </div>
            ) : (
              <div className="space-y-1">
                {filteredPrompts.slice(0, 20).map(prompt => (
                  <button
                    key={prompt.id}
                    onClick={() => handlePromptClick(prompt)}
                    className={cn(
                      'w-full px-3 py-2 text-left rounded-md',
                      'hover:bg-accent hover:shadow-sm transition-all duration-200',
                      'flex items-start gap-2',
                      'cursor-pointer group'
                    )}
                  >
                    <PromptCategoryIcon
                      iconName={PROMPT_CATEGORY_ICONS[prompt.category]}
                      className="mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-colors"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {prompt.title}
                      </div>
                      {prompt.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {prompt.description}
                        </div>
                      )}
                    </div>
                    {isFavorite(prompt.id) && (
                      <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0 mt-1" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Categories view */
          <div className="p-2 space-y-1">
            {/* Favorites section */}
            {favoritePrompts.length > 0 && (
              <div className="mb-2">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Star className="h-3 w-3" />
                  <span>Favorites</span>
                </div>
                <div className="space-y-1">
                  {favoritePrompts.slice(0, 5).map(prompt => (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptClick(prompt)}
                      className={cn(
                        'w-full px-3 py-2 text-left rounded-md',
                        'hover:bg-accent hover:shadow-sm transition-all duration-200',
                        'flex items-start gap-2',
                        'cursor-pointer group'
                      )}
                    >
                      <PromptCategoryIcon
                        iconName={PROMPT_CATEGORY_ICONS[prompt.category]}
                        className="mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-colors"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {prompt.title}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recently used section */}
            {recentlyUsedPrompts.length > 0 && (
              <div className="mb-2">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  <span>Recently Used</span>
                </div>
                <div className="space-y-1">
                  {recentlyUsedPrompts.slice(0, 5).map(prompt => (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptClick(prompt)}
                      className={cn(
                        'w-full px-3 py-2 text-left rounded-md',
                        'hover:bg-accent hover:shadow-sm transition-all duration-200',
                        'flex items-start gap-2',
                        'cursor-pointer group'
                      )}
                    >
                      <PromptCategoryIcon
                        iconName={PROMPT_CATEGORY_ICONS[prompt.category]}
                        className="mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-colors"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {prompt.title}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recently added section */}
            {recentlyAddedPrompts.length > 0 && (
              <div className="mb-2">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" />
                  <span>Recently Added</span>
                </div>
                <div className="space-y-1">
                  {recentlyAddedPrompts.map(prompt => (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptClick(prompt)}
                      className={cn(
                        'w-full px-3 py-2 text-left rounded-md',
                        'hover:bg-accent hover:shadow-sm transition-all duration-200',
                        'flex items-start gap-2',
                        'cursor-pointer group'
                      )}
                    >
                      <PromptCategoryIcon
                        iconName={PROMPT_CATEGORY_ICONS[prompt.category]}
                        className="mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-colors"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {prompt.title}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Divider */}
            {(favoritePrompts.length > 0 || recentlyUsedPrompts.length > 0) && (
              <div className="border-t border-border my-2" />
            )}

            {/* Categories */}
            <div className="space-y-1">
              {PROMPT_CATEGORIES.map(category => {
                const count = categoryCounts[category];
                if (count === 0) return null;

                const isExpanded = expandedCategory === category;
                const categoryPrompts = filteredPrompts.filter(p => p.category === category);

                return (
                  <div key={category}>
                    <button
                      onClick={() => handleCategoryClick(category)}
                      className={cn(
                        'w-full px-3 py-2 text-left rounded-md',
                        'hover:bg-accent transition-colors',
                        'flex items-center justify-between gap-2'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <PromptCategoryIcon iconName={PROMPT_CATEGORY_ICONS[category]} size={16} />
                        <span className="text-sm font-medium text-foreground">
                          {PROMPT_CATEGORY_LABELS[category]}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {count}
                      </span>
                    </button>

                    {/* Expanded prompts in category */}
                    {isExpanded && (
                      <div
                        ref={expandedCategoryRef}
                        className="ml-4 mt-1 space-y-1 animate-in slide-in-from-top-2 duration-200"
                      >
                        {categoryPrompts.map(prompt => (
                          <button
                            key={prompt.id}
                            onClick={() => handlePromptClick(prompt)}
                            className={cn(
                              'w-full px-3 py-2 text-left rounded-md',
                              'hover:bg-accent hover:shadow-sm transition-all duration-200',
                              'flex items-start gap-2',
                              'cursor-pointer group'
                            )}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                                {prompt.title}
                              </div>
                              {prompt.description && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {prompt.description}
                                </div>
                              )}
                            </div>
                            {isFavorite(prompt.id) && (
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0 mt-1" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="border-t border-border p-2 space-y-1">
        <button
          onClick={() => {
            onOpenFullLibrary();
            onClose();
          }}
          className={cn(
            'w-full px-3 py-2 text-left rounded-md',
            'hover:bg-accent transition-colors',
            'flex items-center gap-2',
            'text-sm font-medium text-primary'
          )}
        >
          <Library className="h-4 w-4" />
          <span>Browse All Prompts...</span>
        </button>
      </div>
    </div>
  );

  return createPortal(dropdownContent, document.body);
};
